"use client";

import React, { useState, useCallback } from "react";
import { Plus, Package, RefreshCw } from "lucide-react";
import { useSelector } from "react-redux";
import type { ColumnDef } from "@tanstack/react-table";
import type { RootState } from "@/store/store";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { useDashboardPanel } from "@/hooks/useDashboardPanel";
import { Button } from "@/components/ui/button";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import type { Product, VariantApiShape } from "@/types/models/product";

function formatMoney(amount: number | undefined) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function formatCompactNumber(amount: number | undefined) {
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount || 0);
}

function getProductImage(product: any) {
  const asset =
    product?.images?.[0] ||
    product?.assets?.[0]?.asset?.path ||
    product?.assets?.[0]?.path;
  if (!asset) return null;
  try {
    return getAsset(asset, { width: 72, height: 72, fit: "cover" });
  } catch {
    return null;
  }
}

export default function TopProducts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const reduxProducts = useSelector(
    (state: RootState) => state.dashboard?.topProducts ?? [],
  );
  const { refreshPanel, isRefreshing } = useDashboardPanel("topProducts");

  const displayProducts: Product[] =
    reduxProducts && Array.isArray(reduxProducts) && reduxProducts.length > 0
      ? (reduxProducts as Product[])
      : [];

  const handleAddProduct = () => {
    console.log("Add new product");
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  }, []);

  const getPriceRange = (product: Product) => {
    const variants = product.variants;
    if (!variants || variants.length === 0) {
      return formatMoney(product.price || 0);
    }

    const prices = variants
      .map((v) => {
        const vv = v as VariantApiShape;
        if (typeof vv.price === "number") return vv.price;
        if (typeof vv.listing_price === "number") return vv.listing_price;
        if (typeof vv.listingPrice === "number") return vv.listingPrice;
        return 0;
      })
      .filter((p) => typeof p === "number" && p > 0);

    if (prices.length === 0) return formatMoney(product.price || 0);

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return minPrice === maxPrice
      ? formatMoney(minPrice)
      : `${formatMoney(minPrice)} - ${formatMoney(maxPrice)}`;
  };

  const columns: ColumnDef<Product>[] = [
    {
      accessorKey: "product",
      header: "PRODUCT",
      cell: ({ row }) => {
        const product: any = row.original;
        const image = getProductImage(product);
        const variantCount = product.variants?.length || 0;
        const tags = Array.isArray(product.tags)
          ? product.tags.slice(0, 2)
          : [];

        return (
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 overflow-hidden rounded-lg bg-muted flex-shrink-0">
              {image ? (
                <img
                  src={image}
                  alt={product.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-sm font-medium text-foreground"
                title={product.title}
              >
                {product.title}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {variantCount} variant{variantCount !== 1 ? "s" : ""} ·{" "}
                {product.status || "Draft"}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "inventory",
      header: "STOCK",
      cell: ({ row }) => {
        const product: any = row.original;
        const inventory =
          product.variants?.reduce(
            (sum: number, variant: any) => sum + (variant.inventory || 0),
            0,
          ) ?? 0;
        return (
          <div className="space-y-1">
            <div className="font-semibold text-foreground">
              {formatCompactNumber(inventory)}
            </div>
            <p className="text-xs text-muted-foreground">
              {product.track_inventory ? "Tracked" : "Untracked"} ·{" "}
              {product.taxable ? "Taxable" : "Non-taxable"}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "sales",
      header: "PERFORMANCE",
      cell: ({ row }) => {
        const product: any = row.original;
        return (
          <div className="space-y-1">
            <div className="font-semibold text-foreground">
              {formatCompactNumber(product.units_sold || 0)} sold
            </div>
            <p className="text-xs text-muted-foreground">
              {formatMoney(product.revenue || 0)} revenue ·{" "}
              {formatCompactNumber(product.orders_count || 0)} orders
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "price",
      header: "PRICE",
      cell: ({ row }) => {
        const product: any = row.original;
        return (
          <div className="space-y-1">
            <div className="font-medium text-foreground">
              {getPriceRange(product)}
            </div>
            <p className="text-xs text-muted-foreground">
              Avg {formatMoney(product.average_price || 0)}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "STATUS",
      cell: ({ row }) => {
        const product: any = row.original;
        return (
          <div className="space-y-1">
            <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {String(product.status || "inactive").toLowerCase()}
            </span>
            <p className="text-xs text-muted-foreground">
              {product.revenue_change
                ? `Revenue ${product.revenue_change > 0 ? "+" : ""}${product.revenue_change}%`
                : "Stable"}
            </p>
          </div>
        );
      },
    },
  ];

  return (
    <TableContainer
      className="w-full"
      headerLeft={
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">
            Top Products
          </h2>
          <p className="text-xs text-muted-foreground">
            {formatCompactNumber(
              displayProducts.reduce(
                (sum, p: any) => sum + (p.revenue || 0),
                0,
              ),
            )}{" "}
            revenue across {displayProducts.length} products
          </p>
        </div>
      }
      headerRight={
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshPanel}
          disabled={isRefreshing}
          className="h-8 w-8 p-0"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </Button>
      }
    >
      <DataView
        columns={columns}
        data={displayProducts?.slice(0, 6) || []}
        loading={isRefreshing}
        error={null}
        title=""
        searchQuery={searchQuery}
        onSearch={handleSearch}
        searchPlaceholder="Search products..."
        pagination={{
          page: pageIndex,
          total: Math.min(displayProducts?.length || 0, 6),
          pageSize: 6,
          totalPages: 1,
        }}
        pageIndex={pageIndex}
        onPageChange={setPageIndex}
        actionButtons={[
          {
            label: "Add Product",
            onClick: handleAddProduct,
            variant: "primary",
            icon: <Plus className="w-4 h-4" />,
          },
        ]}
        cardWrapper
        cardWrapperClassName="border-border/60 bg-card/85 backdrop-blur-xl shadow-theme-sm"
        emptyMessage="No products found. Add your first product to get started."
      />
    </TableContainer>
  );
}
